/**
 * Sanctum's CSRF handshake (P0-WEB-04).
 *
 * `apps/api/bootstrap/app.php` calls `statefulApi()`, which puts Laravel's
 * `ValidateCsrfToken` in front of every stateful request. It looks for the
 * token in the `X-XSRF-TOKEN` header. Axios attaches that from the cookie
 * automatically and `fetch` does not — and neither does `createApiClient`,
 * which is a fetch wrapper.
 *
 * Without this file every `POST /v1/auth/login` returns **419** and nothing in
 * the request looks wrong, which is why it is the first thing P0-WEB-04 adds
 * rather than the last.
 */

const COOKIE_NAME = 'XSRF-TOKEN';

/**
 * The route that mints the cookie.
 *
 * Not `/v1/`-prefixed and not in `docs/api/openapi.json`, because Sanctum
 * registers it rather than the application: it is not an endpoint the spec
 * describes. This is the only hand-written fetch in the portal, and it is here
 * rather than at a call site so it stays the only one.
 */
const CSRF_COOKIE_PATH = '/sanctum/csrf-cookie';

/**
 * Laravel URL-encodes the cookie value, and the raw form fails validation with
 * the same 419 as no token at all.
 */
export function readCsrfToken(
  cookieSource: { cookie: string } | undefined = globalThis.document,
): string | undefined {
  // No document at all — the landing site's prerender imports shared code that
  // reaches here. Nothing to read, and throwing would fail a build over a
  // cookie that could not exist yet anyway.
  if (cookieSource === undefined) return undefined;

  for (const part of cookieSource.cookie.split(';')) {
    const separator = part.indexOf('=');
    if (separator === -1) continue;

    if (part.slice(0, separator).trim() === COOKIE_NAME) {
      const raw = part.slice(separator + 1).trim();
      if (raw === '') return undefined;
      try {
        return decodeURIComponent(raw);
      } catch {
        // A malformed value is worse than none: sending it guarantees a 419
        // loop, where returning undefined lets the caller mint a fresh one.
        return undefined;
      }
    }
  }
  return undefined;
}

export type CsrfRefresher = () => Promise<void>;

/**
 * Fetches a fresh CSRF cookie, once, however many callers ask at the same time.
 *
 * Single-flight matters on a page whose loaders fire in parallel: without it, a
 * cold load that starts four requests mints four cookies, and each new one
 * invalidates the token the previous requests are about to send. The symptom is
 * a 419 that only ever reproduces under concurrency.
 */
export function createCsrfRefresher(
  baseUrl: string,
  fetchImpl: typeof globalThis.fetch,
): CsrfRefresher {
  let inFlight: Promise<void> | undefined;

  return function refresh(): Promise<void> {
    inFlight ??= fetchImpl(baseUrl + CSRF_COOKIE_PATH, {
      method: 'GET',
      // The cookie is the entire point; without this it is dropped on receipt.
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then(() => undefined)
      .finally(() => {
        // Cleared whether it resolved or rejected, so a refresh that failed
        // because the network was down does not poison every later attempt.
        inFlight = undefined;
      });

    return inFlight;
  };
}
